import { Link, useParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { SEOHead } from '@/components/feature/SEOHead';
import { SchemaOrg } from '@/components/feature/SchemaOrg';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <Layout>
        <div className="container-page py-20 text-center">
          <h1 className="font-heading text-2xl font-semibold text-foreground-900 mb-4">
            文章未找到
          </h1>
          <p className="text-foreground-600 mb-6">抱歉，您查找的文章不存在或已被移除。</p>
          <Link
            to="/"
            className="bg-primary-500 hover:bg-primary-600 text-white px-6 py-2.5 rounded-md font-medium text-sm transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            返回首页
          </Link>
        </div>
      </Layout>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const articleSchema = {
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    dateModified: article.date,
    image: article.image,
    publisher: {
      '@type': 'Organization',
      name: '含羞草研究院',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.mimosa-research.cn/article/${article.id}`,
    },
    keywords: article.tags.join(','),
  };

  const breadcrumbSchema = {
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://www.mimosa-research.cn',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.categoryName,
        item: `https://www.mimosa-research.cn/category/${article.category}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
      },
    ],
  };

  return (
    <Layout>
      <SEOHead
        title={`${article.title} - 含羞草研究院`}
        description={article.excerpt}
        keywords={`${article.tags.join(',')},含羞草研究院,含羞草研究院学习资源`}
        canonicalUrl={`https://www.mimosa-research.cn/article/${article.id}`}
        ogType="article"
        ogImage={article.image}
        ogImageAlt={article.title}
        articlePublishedTime={article.date}
        articleModifiedTime={article.date}
        articleAuthor={article.author}
        articleSection={article.categoryName}
        articleTags={article.tags}
      />
      <SchemaOrg schema={articleSchema} />
      <SchemaOrg schema={breadcrumbSchema} />

      <div className="bg-background-100 border-b border-background-200/60">
        <div className="container-page py-6">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: article.categoryName, to: `/category/${article.category}` },
              { label: article.title },
            ]}
          />
        </div>
      </div>

      <article className="container-page py-10 md:py-14">
        <div className="max-w-3xl mx-auto">
          <header className="mb-8 md:mb-10">
            <span className="bg-primary-100 text-primary-700 text-xs px-3 py-1 rounded-md whitespace-nowrap">
              {article.categoryName}
            </span>
            <h1 className="font-heading text-2xl md:text-4xl font-semibold text-foreground-900 mt-4 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <div className="flex items-center gap-2">
                <img
                  src={article.authorAvatar}
                  alt={article.author}
                  className="w-7 h-7 rounded-full object-cover"
                />
                <span className="font-medium text-foreground-700">{article.author}</span>
              </div>
              <span className="text-foreground-300">|</span>
              <span>{article.date}</span>
              <span className="text-foreground-300">|</span>
              <span>{article.readTime}</span>
            </div>
          </header>

          <div className="w-full aspect-[16/9] rounded-lg overflow-hidden mb-8 md:mb-10">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="prose-custom">
            {article.content.map((block, i) => {
              if (block.type === 'heading') {
                const Tag = i === 0 ? 'h2' as const : 'h2' as const;
                return (
                  <Tag
                    key={i}
                    className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mt-8 mb-4"
                    id={`section-${i}`}
                  >
                    <a href={`#section-${i}`} className="no-underline">
                      {block.text}
                    </a>
                  </Tag>
                );
              }
              return (
                <p key={i} className="text-foreground-700 leading-relaxed mb-4 text-sm md:text-base">
                  {block.text}
                </p>
              );
            })}
          </div>

          <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-background-200/60">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="bg-background-100 text-foreground-600 text-xs px-3 py-1.5 rounded-full"
              >
                #{tag}
              </span>
            ))}
          </div>
        </div>
      </article>

      {relatedArticles.length > 0 && (
        <section className="bg-background-100 py-10 md:py-14">
          <div className="container-page">
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-6 md:mb-8">
              相关文章
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {relatedArticles.map((rel) => (
                <Link
                  key={rel.id}
                  to={`/article/${rel.id}`}
                  className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/60 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative w-full aspect-[16/10] overflow-hidden">
                    <img
                      src={rel.image}
                      alt={rel.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
                      {rel.title}
                    </h3>
                    <p className="text-xs text-foreground-500 line-clamp-2">
                      {rel.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
}